import { type CSSProperties, useMemo, useState } from "react";
import { t } from "ttag";

import { IconButtonWrapper } from "metabase/common/components/IconButtonWrapper";
import { isColumnHiddenFromQuestionPickers } from "metabase/dashboard/utils/add-custom-filter";
import { isEmbedding } from "metabase/embedding/config";
import { METAKEY } from "metabase/utils/browser";
import { Icon, Popover, Tooltip } from "metabase/ui";
import { METAKEY } from "metabase/utils/browser";
import * as Lib from "metabase-lib";

import type { NotebookStepProps } from "../../types";
import { FieldPicker, type FieldPickerItem } from "../FieldPicker";
import { useNotebookContext } from "../Notebook/context";
import { NotebookCell, NotebookCellItem } from "../NotebookCell";
import { CONTAINER_PADDING } from "../NotebookCell/constants";
import { NotebookDataPicker } from "../NotebookDataPicker";
import { DataPickerTarget } from "../NotebookDataPicker/DataPickerTarget";

import S from "./DataStep.module.css";

export const DataStep = ({
  query,
  step,
  readOnly = false,
  color,
  updateQuery,
}: NotebookStepProps) => {
  const { dataPickerOptions } = useNotebookContext();
  const { question, stageIndex } = step;
  const tableId = Lib.sourceTableOrCardId(query);
  const table = tableId
    ? (Lib.tableOrCardMetadata(query, tableId) ?? undefined)
    : undefined;
  const [isOpened, setIsOpened] = useState(() => !table);
  const isMetric = question.type() === "metric";

  const isRaw = useMemo(() => {
    return (
      Lib.aggregations(query, stageIndex).length === 0 &&
      Lib.breakouts(query, stageIndex).length === 0
    );
  }, [query, stageIndex]);

  const canSelectTableColumns = table && isRaw && !readOnly;
  const isEmbed = isEmbedding();

  const handleTableChange = async (
    table: Lib.TableMetadata | Lib.CardMetadata,
    metadataProvider: Lib.MetadataProvider,
  ) => {
    const newQuery = Lib.queryFromTableOrCardMetadata(metadataProvider, table);
    const newAggregations = Lib.aggregations(newQuery, stageIndex);
    if (isMetric && newAggregations.length === 0) {
      await updateQuery(Lib.aggregateByCount(newQuery, stageIndex));
    } else {
      await updateQuery(newQuery);
    }
  };

  return (
    <NotebookCell color={color}>
      {isOpened || !table || isEmbed ? (
        <NotebookDataPicker
          query={query}
          stageIndex={stageIndex}
          table={table}
          title={t`Pick your starting data`}
          canChangeDatabase
          hasMetrics
          isOpened={isOpened}
          setIsOpened={setIsOpened}
          isDisabled={readOnly}
          onChange={handleTableChange}
          columnPicker={
            <DataFieldPopover
              query={query}
              stageIndex={stageIndex}
              updateQuery={updateQuery}
            />
          }
          {...dataPickerOptions}
        />
      ) : (
        <NotebookCellItem
          color={color}
          inactive={!table}
          right={
            canSelectTableColumns && (
              <DataFieldPopover
                query={query}
                stageIndex={stageIndex}
                updateQuery={updateQuery}
              />
            )
          }
          containerStyle={{ padding: 0 }}
          rightContainerStyle={{ width: 37, padding: 0 }}
          data-testid="data-step-cell"
          disabled={readOnly}
        >
          <Tooltip
            label={t`${METAKEY}+click to open in new tab`}
            hidden={!table || readOnly}
            events={{ hover: true, focus: false, touch: false }}
          >
            <DataPickerTarget
              table={table}
              query={query}
              setIsOpened={setIsOpened}
              stageIndex={stageIndex}
              isDisabled={readOnly}
            />
          </Tooltip>
        </NotebookCellItem>
      )}
    </NotebookCell>
  );
};

interface DataFieldPopoverProps {
  query: Lib.Query;
  stageIndex: number;
  updateQuery: (query: Lib.Query) => Promise<void>;
}

export function DataFieldPopover({
  query,
  stageIndex,
  updateQuery,
}: DataFieldPopoverProps) {
  return (
    <Popover position="bottom-start">
      <Popover.Target>
        <Tooltip label={t`Pick columns`}>
          <IconButtonWrapper
            className={S.DataStepIconButton}
            style={
              {
                "--notebook-cell-container-padding": CONTAINER_PADDING,
              } as CSSProperties
            }
            aria-label={t`Pick columns`}
            data-testid="fields-picker"
          >
            <Icon name="chevrondown" />
          </IconButtonWrapper>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <DataFieldPicker
          query={query}
          stageIndex={stageIndex}
          updateQuery={updateQuery}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

interface DataFieldPickerProps {
  query: Lib.Query;
  stageIndex: number;
  updateQuery: (query: Lib.Query) => Promise<void>;
}

function DataFieldPicker({
  query,
  stageIndex,
  updateQuery,
}: DataFieldPickerProps) {
  const columns = useMemo(() => {
    const allColumns = Lib.fieldableColumns(query, stageIndex);
    return allColumns.filter(
      (col) =>
        !isColumnHiddenFromQuestionPickers(
          Lib.displayInfo(query, stageIndex, col).name,
        ),
    );
  }, [query, stageIndex]);

  const handleToggle = (column: Lib.ColumnMetadata, isSelected: boolean) => {
    const nextQuery = isSelected
      ? Lib.addField(query, stageIndex, column)
      : Lib.removeField(query, stageIndex, column);
    updateQuery(nextQuery);
  };

  const handleSelectAll = () => {
    const nextQuery = Lib.withFields(query, stageIndex, []);
    updateQuery(nextQuery);
  };

  const handleSelectNone = () => {
    const nextQuery = Lib.withFields(query, stageIndex, [columns[0]]);
    updateQuery(nextQuery);
  };

  return (
    <FieldPicker
      query={query}
      stageIndex={stageIndex}
      columns={columns}
      isColumnSelected={isColumnSelected}
      isColumnDisabled={isColumnDisabled}
      onToggle={handleToggle}
      onSelectAll={handleSelectAll}
      onSelectNone={handleSelectNone}
    />
  );
}

function isColumnSelected({ columnInfo }: FieldPickerItem) {
  return Boolean(columnInfo.selected);
}

function isColumnDisabled(item: FieldPickerItem, items: FieldPickerItem[]) {
  const isSelected = isColumnSelected(item);
  const isOnlySelected = items.filter(isColumnSelected).length === 1;
  return isSelected && isOnlySelected;
}
